<template>
	<div class="projects-content px-5">
		<div class="projects-toolbar">
			<div class="projects-search">
				<v-icon>search</v-icon>
				<input v-model="search" type="search" placeholder="Buscar proyecto..."
					@keyup.enter="searchProjects">
				<button v-if="search" type="button" aria-label="Limpiar búsqueda" @click="clearSearch">
					<v-icon>close</v-icon>
				</button>
			</div>
			<button type="button" class="search-button" @click="searchProjects">Buscar</button>
		</div>

		<div v-if="rows.length" class="project-grid">
			<article v-for="project in rows" :key="project.id" class="project-card">
				<header class="project-card-header">
					<span class="project-avatar"><v-icon color="white">folder</v-icon></span>
					<span class="project-id">ID {{ project.id }}</span>
				</header>
				<div class="project-card-body">
					<h2>{{ project.name }}</h2>
					<span class="project-code">{{ project.codeProject }}</span>
					<p>{{ project.description || 'Sin descripción disponible' }}</p>
				</div>
				<footer class="project-card-footer">
					<button type="button" class="edit-button" @click="edit(project)">
						<v-icon>edit</v-icon> Editar
					</button>
					<v-menu offset-y left content-class="project-menu" min-width="230">
						<button slot="activator" type="button" class="manage-button">
							Administrar <v-icon>expand_more</v-icon>
						</button>
						<v-list dense>
							<v-list-tile class="menu-company" @click="openCompanies(project)"><v-list-tile-action><v-icon color="primary">business</v-icon></v-list-tile-action><v-list-tile-title>Compañías</v-list-tile-title></v-list-tile>
							<v-list-tile class="menu-apps" @click="openApps(project)"><v-list-tile-action><v-icon color="deep-purple">apps</v-icon></v-list-tile-action><v-list-tile-title>Aplicaciones</v-list-tile-title></v-list-tile>
							<v-list-tile class="menu-roles" @click="goToGlobalRoles(project)"><v-list-tile-action><v-icon color="green">language</v-icon></v-list-tile-action><v-list-tile-title>Roles globales</v-list-tile-title></v-list-tile>
							<v-list-tile class="menu-template" @click="openTemplate(project)"><v-list-tile-action><v-icon color="orange">description</v-icon></v-list-tile-action><v-list-tile-title>Templates</v-list-tile-title></v-list-tile>
							<v-list-tile class="menu-actions" @click="openActions(project)"><v-list-tile-action><v-icon color="pink">touch_app</v-icon></v-list-tile-action><v-list-tile-title>Barra de acciones</v-list-tile-title></v-list-tile>
							<v-list-tile class="menu-services" @click="goToWebServices(project)"><v-list-tile-action><v-icon color="cyan">cloud_queue</v-icon></v-list-tile-action><v-list-tile-title>Web Services</v-list-tile-title></v-list-tile>
							<v-list-tile class="menu-process" @click="openProcesses(project)"><v-list-tile-action><v-icon color="blue-grey">build</v-icon></v-list-tile-action><v-list-tile-title>Procesos</v-list-tile-title></v-list-tile>
							<v-list-tile class="menu-plan" @click="createPlan(project)"><v-list-tile-action><v-icon color="amber darken-3">request_quote</v-icon></v-list-tile-action><v-list-tile-title>Crear plan</v-list-tile-title></v-list-tile>
							<v-divider></v-divider>
							<v-list-tile class="delete-option" @click="openModalConfirmation(project)"><v-list-tile-action><v-icon color="error">delete_outline</v-icon></v-list-tile-action><v-list-tile-title>Eliminar proyecto</v-list-tile-title></v-list-tile>
						</v-list>
					</v-menu>
				</footer>
			</article>
		</div>
		<div v-else class="projects-empty">
			<v-icon>folder_open</v-icon>
			<h2>No hay proyectos para mostrar</h2>
			<p>Prueba con otro término de búsqueda o crea un nuevo proyecto.</p>
		</div>

		<nav v-if="rows.length && lastPage > 1" class="projects-pagination" aria-label="Paginación">
			<button type="button" :disabled="currentPage <= 1" @click="changePage(currentPage - 1)">
				<v-icon>chevron_left</v-icon> Anterior
			</button>
			<span>Página <strong>{{ currentPage }}</strong> de {{ lastPage }}</span>
			<button type="button" :disabled="currentPage >= lastPage" @click="changePage(currentPage + 1)">
				Siguiente <v-icon>chevron_right</v-icon>
			</button>
		</nav>
		<app-confirmation-modal
			:loading="loading"
			:show-modal="modalConfirmation.value"
			@cancel="cancelModalConfirmation"
			@confirm="confirm()"
		/>
	</div>
</template>

<script>
import appConfirmationModal from '@/components/shared/app-confirmation-modal';
import helper from '@/shared/helper';

const { setLocalData } = helper;

function changePage(page) {
	this.$emit('change-page', page);
}

function confirm() {
	this.loading = true;
	this.$http
		.delete(`project/${this.modalConfirmation.selectedRow.id}`)
		.then(() => {
			this.showNotification('Registro eliminado correctamente');
		})
		.catch((err) => {
			if (err.status === 405) {
				if (err.data.message === 'No se elimino el proyecto, tiene modulos asociados.') {
					this.showError('El proyecto no pudo ser eliminado por tener data asociada.');
				}
			}
		})
		.finally(() => {
			this.loading = false;
			this.modalConfirmation.value = false;
			this.$emit('on-delete');
		});
}

function edit({ id }) {
	this.goTo('page-project-edit', { params: { id } });
}

function openProcesses({ codeProject }) {
	this.goTo('page-process', { params: { codeProject } });
}

function onSearch(value) {
	this.$emit('on-search', value);
}

function searchProjects() {
	this.onSearch(this.search.trim());
}

function clearSearch() {
	this.search = '';
	this.onSearch('');
}

function openActions({ codeProject, languages }) {
	setLocalData('project-languages', languages);
	this.goTo('page-action', { params: { codeProject } });
}

function openApps({ codeProject, languages }) {
	setLocalData('project-languages', languages);
	this.goTo('page-app', { params: { codeProject } });
}

function openCompanies({ codeProject }) {
	this.goTo('page-company', { params: { codeProject } });
}

function openTemplate({ codeProject, id }) {
	setLocalData('project-id', id);
	this.goTo('page-template', { params: { codeProject, projectId: id } });
}

function goToGlobalRoles(project) {
	const { codeProject, id: projectId } = project;
	this.goTo(
		'roles-global',
		{ params: { codeProject, projectId } },
	);
}

function goToWebServices({ codeProject }) {
	this.goTo('page-web-services', { params: { codeProject } });
}

function createPlan({ codeProject }) {
	this.goTo('page-plan', { params: { codeProject } });
}

function data() {
	return {
		loading: false,
		search: '',
	};
}

export default {
	name: 'projects-list',
	components: {
		appConfirmationModal,
	},
	data,
	props: {
		columns: {
			type: Array,
			required: true,
		},
		currentPage: {
			type: Number,
			default: 0,
		},
		lastPage: {
			type: Number,
			default: 0,
		},
		rows: {
			type: Array,
			required: true,
		},
	},
	methods: {
		changePage,
		clearSearch,
		confirm,
		edit,
		goToGlobalRoles,
		goToWebServices,
		onSearch,
		searchProjects,
		openActions,
		openApps,
		openCompanies,
		openProcesses,
		openTemplate,
		createPlan,
	},
};
</script>

<style lang="scss" scoped>
.projects-content { padding-top: 18px; }
.projects-overview {
	align-items: center;
	background: linear-gradient(135deg, #fff, #F4F8F6);
	border: 1px solid #DCE9E4;
	border-radius: 14px;
	display: flex;
	justify-content: space-between;
	margin-bottom: 14px;
	padding: 18px 22px;
	h1 { color: #172b4d; font-family: map-get($fonts, open-semibold); font-size: 23px; margin: 3px 0; }
	p { color: #718096; font-size: 12px; margin: 0; }
}
.overview-eyebrow { color: #1A4338; font-size: 9px; font-weight: 700; letter-spacing: .13em; }
.projects-count {
	align-items: center;
	background: #EAF3EF;
	border-radius: 10px;
	color: #3F6258;
	display: flex;
	font-size: 11px;
	gap: 9px;
	padding: 9px 13px;
	.icon { color: #2E6B59; font-size: 19px; }
	strong { color: #1A4338; font-size: 14px; }
}
.projects-toolbar {
	align-items: center;
	background: white;
	border: 1px solid #DCE9E4;
	border-radius: 12px;
	display: flex;
	gap: 8px;
	margin-bottom: 16px;
	padding: 9px;
}
.projects-search {
	align-items: center;
	background: #f7f9fc;
	border: 1px solid #DCE9E4;
	border-radius: 9px;
	display: flex;
	height: 40px;
	max-width: 420px;
	padding: 0 12px;
	width: 100%;
	> .icon { color: #8294a8; font-size: 19px; margin-right: 8px; }
	input { background: transparent; border: 0; color: #29425d; font-size: 12px; outline: none; width: 100%; }
	button { background: transparent; border: 0; cursor: pointer; display: flex; padding: 3px; .icon { color: #8294a8; font-size: 17px; } }
	&:focus-within { background: white; border-color: #2E6B59; box-shadow: 0 0 0 3px rgba(46, 107, 89, .12); }
}
.search-button {
	background: #1A4338;
	border: 0;
	border-radius: 8px;
	color: white;
	cursor: pointer;
	font-size: 11px;
	font-weight: 600;
	height: 40px;
	padding: 0 18px;
}
.project-grid { display: grid; gap: 16px; grid-template-columns: repeat(3, minmax(0, 1fr)); }
.project-card {
	background: white;
	border: 1px solid #DCE9E4;
	border-radius: 14px;
	box-shadow: 0 4px 14px rgba(31, 57, 85, .06);
	display: flex;
	flex-direction: column;
	min-height: 220px;
	overflow: hidden;
	transition: border-color .2s ease, box-shadow .2s ease, transform .2s ease;
	&:hover { border-color: #BFD5CC; box-shadow: 0 12px 28px rgba(26, 67, 56, .12); transform: translateY(-2px); }
}
.project-card-header { align-items: center; display: flex; justify-content: space-between; padding: 18px 18px 0; }
.project-avatar {
	align-items: center;
	background: linear-gradient(135deg, #2E6B59, #1A4338);
	border-radius: 10px;
	display: inline-flex;
	height: 40px;
	justify-content: center;
	width: 40px;
	.icon { font-size: 20px; }
}
.project-id { color: #9aabba; font-size: 9px; font-weight: 700; letter-spacing: .08em; }
.project-card-body {
	flex: 1;
	padding: 16px 18px;
	h2 { color: #213b56; font-family: map-get($fonts, open-semibold); font-size: 15px; margin: 0 0 10px; }
	p { color: #718396; font-size: 11px; line-height: 1.55; margin: 14px 0 0; }
}
.project-code { background: #eef3f8; border-radius: 6px; color: #52667a; font-family: monospace; font-size: 11px; padding: 5px 8px; }
.project-card-footer { align-items: center; background: #f9fbfd; border-top: 1px solid #e9eef4; display: flex; justify-content: space-between; padding: 10px 13px; }
.edit-button {
	align-items: center;
	background: transparent;
	border: 0;
	border-radius: 7px;
	color: #4d6780;
	cursor: pointer;
	display: flex;
	font-size: 11px;
	font-weight: 600;
	gap: 5px;
	height: 34px;
	padding: 0 9px;
	.icon { color: #3B82C4; font-size: 17px; }
	&:hover { background: #EAF3FA; color: #2F6F9F; }
}
.manage-button {
	align-items: center;
	background: #EEF5F2;
	border: 1px solid #C8DDD5;
	border-radius: 8px;
	color: #1A4338;
	cursor: pointer;
	display: inline-flex;
	font-size: 11px;
	font-weight: 600;
	gap: 3px;
	height: 34px;
	margin-left: 5px;
	outline: none;
	padding: 0 10px;
	.icon { color: #1A4338; font-size: 17px; }
	&:hover { background: #E2EFE9; }
}
.projects-empty {
	background: white;
	border: 1px dashed #ccd9e6;
	border-radius: 14px;
	color: #8495a7;
	padding: 48px 20px;
	text-align: center;
	> .icon { color: #82A69A; font-size: 42px; }
	h2 { color: #405a73; font-size: 15px; margin: 12px 0 5px; }
	p { font-size: 11px; margin: 0; }
}
.projects-pagination {
	align-items: center;
	display: flex;
	justify-content: flex-end;
	margin-top: 16px;
	gap: 14px;
	span { color: #74869a; font-size: 11px; }
	button { align-items: center; background: white; border: 1px solid #C8DDD5; border-radius: 8px; color: #1A4338; cursor: pointer; display: flex; font-size: 11px; height: 34px; padding: 0 9px; }
	button:disabled { cursor: default; opacity: .45; }
	.icon { color: inherit; font-size: 17px; }
}
@media (max-width: 1050px) { .project-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 700px) {
	.projects-content { padding-left: 12px !important; padding-right: 12px !important; }
	.projects-overview { align-items: flex-start; flex-direction: column; gap: 13px; }
	.project-grid { grid-template-columns: 1fr; }
	.projects-pagination { justify-content: center; }
}
</style>

<style lang="scss">
.project-menu {
	border: 1px solid #DCE9E4;
	border-radius: 12px;
	box-shadow: 0 14px 36px rgba(22, 50, 79, .2) !important;
	overflow: hidden;

	.list { padding: 7px; }
	.list__tile {
		border-radius: 8px;
		height: 42px;
		margin: 2px 0;
		transition: background .18s ease, transform .18s ease;
		&:hover { background: #EEF5F2; transform: translateX(2px); }
	}
	.list__tile__action {
		align-items: center;
		border-radius: 8px;
		display: flex;
		height: 30px;
		justify-content: center;
		margin-right: 12px;
		min-width: 30px;
		width: 30px;
		.icon { font-size: 17px; }
	}
	.list__tile__title { color: #334e68; font-size: 12px; font-weight: 600; }
	.menu-company .list__tile__action { background: #EAF3EF; .icon { color: #1A4338; } }
	.menu-apps .list__tile__action { background: #f1eaff; .icon { color: #7c3aed; } }
	.menu-roles .list__tile__action { background: #e5f8ef; .icon { color: #15915c; } }
	.menu-template .list__tile__action { background: #fff2df; .icon { color: #dd7a08; } }
	.menu-actions .list__tile__action { background: #fff0f5; .icon { color: #db3d77; } }
	.menu-services .list__tile__action { background: #e5f8fb; .icon { color: #0891a8; } }
	.menu-process .list__tile__action { background: #edf0f5; .icon { color: #52677f; } }
	.menu-plan .list__tile__action { background: #fff8dc; .icon { color: #b88a00; } }
	.delete-option {
		.list__tile__action { background: #fff0f0; }
		.list__tile__title { color: #d33f49; }
		.list__tile:hover { background: #fff3f3; }
	}
}
</style>



// WEBPACK FOOTER //
// src/components/projects/project-list.vue